// components/AdminStatistics.js - Complete version with hierarchical support
import React, { useState, useEffect } from 'react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import './AdminStatistics.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const AdminStatistics = ({ onBack, user }) => {
  const [managedUsers, setManagedUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [allLogs, setAllLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Hierarchical data
  const [hierarchyInfo, setHierarchyInfo] = useState(null);
  const [isHierarchical, setIsHierarchical] = useState(false);
  
  // Aggregation controls
  const [aggregationType, setAggregationType] = useState('day');
  const [numPeriods, setNumPeriods] = useState(7);
  const [tempPeriods, setTempPeriods] = useState(7);

  // Field order - sorted by point value (highest at bottom of stack = most prominent)
  const fieldOrder = [
    'email',              // 1 pt - top of stack
    'call',               // 1 pt
    'opp_created',        // 2 pts
    'knock',              // 2 pts
    'networking_event',   // 5 pts
    'ac_partner',         // 5 pts
    'motis_appointment',  // 10 pts
    'appt_attended',      // 10 pts
    'appt_set',           // 10 pts
    'sale',               // 20 pts - bottom of stack (most visible)
  ];

  const fieldLabels = {
    knock: 'Knock',
    call: 'Call',
    opp_created: 'Opp Created',
    appt_set: 'Appt Set',
    appt_attended: 'Appt Attended',
    sale: 'Sale',
    ac_partner: 'AC Partner',
    networking_event: 'Networking',
    motis_appointment: 'MOTIS Appt',
    email: 'Email',
  };

  // Original color palette
  const fieldColors = {
    knock: '#e57373',
    call: '#81c784',
    opp_created: '#64b5f6',
    appt_set: '#ffb74d',
    appt_attended: '#9575cd',
    sale: '#4db6ac',
    ac_partner: '#f06292',
    networking_event: '#dce775',
    motis_appointment: '#ff8a65',
    email: '#ba68c8',
  };

  const pointValues = {
    knock: 2,
    call: 1,
    opp_created: 2,
    appt_set: 10,
    appt_attended: 10,
    sale: 20,
    ac_partner: 5,
    networking_event: 5,
    motis_appointment: 10,
    email: 1,
  };

  useEffect(() => {
    fetchManagedUsers();
  }, []);

  useEffect(() => {
    if (selectedUserId) {
      fetchHierarchyInfo();
    }
  }, [selectedUserId]);

  useEffect(() => {
    if (selectedUserId && hierarchyInfo !== null) {
      fetchUserLogs();
    }
  }, [selectedUserId, hierarchyInfo]);

  useEffect(() => {
    if (allLogs.length > 0) {
      processData();
    } else {
      setChartData([]);
    }
  }, [allLogs, aggregationType, numPeriods]);

  const fetchManagedUsers = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/managed-users/`, {
        headers: {
          'X-User-ID': user.userId,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setManagedUsers(data);
        
        // Auto-select first user if available
        if (data.length > 0) {
          setSelectedUserId(data[0].id.toString());
          setSelectedUser(data[0]);
        }
      }
    } catch (error) {
      setError('Error fetching managed users');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHierarchyInfo = async () => {
    if (!selectedUserId) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/hierarchy-info/${selectedUserId}/`, {
        headers: {
          'X-User-ID': user.userId,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setHierarchyInfo(data);
        setIsHierarchical(data.total_users_in_hierarchy > 1);
      }
    } catch (error) {
      console.error('Error fetching hierarchy info:', error);
      setHierarchyInfo({});
      setIsHierarchical(false);
    }
  };

  const fetchUserLogs = async () => {
    if (!selectedUserId) return;
    
    setError('');
    try {
      // Check if we should use hierarchical endpoint
      const shouldUseHierarchical = hierarchyInfo && hierarchyInfo.total_users_in_hierarchy > 1;
      
      const endpoint = shouldUseHierarchical ? 
        `${API_URL}/api/admin/hierarchical-logs/${selectedUserId}/` :
        `${API_URL}/api/admin/user-logs/${selectedUserId}/`;
        
      const response = await fetch(endpoint, {
        headers: {
          'X-User-ID': user.userId,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch user logs');
      }
      
      const data = await response.json();
      
      // Handle both regular logs and hierarchical logs
      if (data.logs) {
        // Hierarchical response - data already contains points
        setAllLogs(data.logs);
        setIsHierarchical(data.is_hierarchical);
      } else {
        // Regular response - convert counts to points
        const logsWithPoints = data.map(log => {
          const logWithPoints = { ...log };
          fieldOrder.forEach(field => {
            // Convert count to points for regular logs
            logWithPoints[field] = (log[field] || 0);
            // Store original count for tooltips
            logWithPoints[`${field}_count`] = log[field] || 0;
          });
          return logWithPoints;
        });
        setAllLogs(logsWithPoints);
        setIsHierarchical(false);
      }
    } catch (err) {
      setError('Error fetching user statistics. Please try again.');
      console.error('Error:', err);
    }
  };

  const handleUserChange = (e) => {
    const userId = e.target.value;
    setSelectedUserId(userId);
    
    const user = managedUsers.find(u => u.id.toString() === userId);
    setSelectedUser(user);
    
    // Reset hierarchy info when changing users
    setHierarchyInfo(null);
    setIsHierarchical(false);
    setAllLogs([]);
  };

  const processData = () => {
    switch (aggregationType) {
      case 'day':
        setChartData(processDailyData());
        break;
      case 'week':
        setChartData(processWeeklyData());
        break;
      case 'month':
        setChartData(processMonthlyData());
        break;
      default:
        setChartData([]);
    }
  };

  const processDailyData = () => {
    const data = [];
    const today = new Date();
    
    for (let i = numPeriods - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      
      const dayData = {
        label: formatDate(date),
        ...getEmptyFieldsObject(),
        period: toLocalISODate(date),
        total: 0
      };
      
      const log = allLogs.find(log => log.date === dayData.period);
      if (log) {
        fieldOrder.forEach(field => {
          if (isHierarchical) {
            // Hierarchical data already contains points
            dayData[field] = log[field] || 0;
            // Calculate count from points for tooltip (approximate)
            dayData[`${field}_count`] = Math.round((log[field] || 0) / pointValues[field]);
          } else {
            // Regular data needs conversion to points
            const points = (log[field] || 0) * pointValues[field];
            dayData[field] = points;
            dayData[`${field}_count`] = log[field] || 0;
          }
          dayData.total += dayData[field];
        });
      }
      
      data.push(dayData);
    }
    
    return data;
  };

  const processWeeklyData = () => {
    const data = [];
    const today = new Date();
    
    for (let i = numPeriods - 1; i >= 0; i--) {
      const endDate = new Date(today);
      endDate.setDate(today.getDate() - (i * 7));
      
      const startDate = new Date(endDate);
      const currentDay = endDate.getDay();
      const daysToSubtract = currentDay === 0 ? 6 : currentDay - 1;
      startDate.setDate(endDate.getDate() - daysToSubtract);
      
      const weekData = {
        label: `${formatDateShort(startDate)} - ${formatDateShort(endDate)}`,
        ...getEmptyFieldsObject(),
        period: toLocalISODate(startDate),
        total: 0
      };

      const weekLogs = allLogs.filter(log => {
        const logDate = parseLocalDate(log.date);
        return logDate >= startDate && logDate <= endDate;
      });
      
      weekLogs.forEach(log => {
        fieldOrder.forEach(field => {
          if (isHierarchical) {
            // Hierarchical data already contains points
            weekData[field] += log[field] || 0;
            weekData[`${field}_count`] = (weekData[`${field}_count`] || 0) + Math.round((log[field] || 0) / pointValues[field]);
          } else {
            // Regular data needs conversion to points
            const points = (log[field] || 0) * pointValues[field];
            weekData[field] += points;
            weekData[`${field}_count`] = (weekData[`${field}_count`] || 0) + (log[field] || 0);
          }
          weekData.total += (isHierarchical ? (log[field] || 0) : ((log[field] || 0) * pointValues[field]));
        });
      });
      
      data.push(weekData);
    }
    
    return data;
  };

  const processMonthlyData = () => {
    const data = [];
    const today = new Date();
    
    for (let i = numPeriods - 1; i >= 0; i--) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
      const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      
      const monthData = {
        label: date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        ...getEmptyFieldsObject(),
        period: toLocalISODate(monthStart),
        total: 0
      };

      const monthLogs = allLogs.filter(log => {
        const logDate = parseLocalDate(log.date);
        return logDate >= monthStart && logDate <= monthEnd;
      });
      
      monthLogs.forEach(log => {
        fieldOrder.forEach(field => {
          if (isHierarchical) {
            // Hierarchical data already contains points
            monthData[field] += log[field] || 0;
            monthData[`${field}_count`] = (monthData[`${field}_count`] || 0) + Math.round((log[field] || 0) / pointValues[field]);
          } else {
            // Regular data needs conversion to points
            const points = (log[field] || 0) * pointValues[field];
            monthData[field] += points;
            monthData[`${field}_count`] = (monthData[`${field}_count`] || 0) + (log[field] || 0);
          }
          monthData.total += (isHierarchical ? (log[field] || 0) : ((log[field] || 0) * pointValues[field]));
        });
      });
      
      data.push(monthData);
    }
    
    return data;
  };

  const getEmptyFieldsObject = () => {
    const fields = {};
    fieldOrder.forEach(field => {
      fields[field] = 0;
      fields[`${field}_count`] = 0;
    });
    return fields;
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateShort = (date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  // Format date as YYYY-MM-DD in LOCAL timezone (not UTC)
  const toLocalISODate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Parse a YYYY-MM-DD string as LOCAL date (not UTC)
  const parseLocalDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const handleAggregationChange = (e) => {
    const newType = e.target.value;
    setAggregationType(newType);
    
    switch (newType) {
      case 'day':
        setNumPeriods(7);
        setTempPeriods(7);
        break;
      case 'week':
        setNumPeriods(4);
        setTempPeriods(4);
        break;
      case 'month':
        setNumPeriods(6);
        setTempPeriods(6);
        break;
    }
  };

  const handlePeriodsSubmit = (e) => {
    e.preventDefault();
    setNumPeriods(Math.max(1, parseInt(tempPeriods) || 1));
  };

  const customTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const firstPayload = payload[0];
      const totalPoints = firstPayload && firstPayload.payload ? firstPayload.payload.total : 0;
      
      return (
        <div className="custom-tooltip">
          <p className="label">{label}</p>
          {isHierarchical && (
            <p className="hierarchy-note">Team Hierarchy Data</p>
          )}
          {payload.map((entry, index) => {
            const field = entry.dataKey;
            const points = entry.value || 0;
            const count = entry.payload && entry.payload[`${field}_count`] ? entry.payload[`${field}_count`] : 0;
            const pointValue = pointValues[field] || 0;
            
            return points > 0 && (
              <div key={index}>
                <p style={{ color: entry.color }}>
                  {`${fieldLabels[field]}: ${points} points`}
                </p>
                {!isHierarchical && (
                  <p style={{ fontSize: '0.85em', color: '#8a90a0' }}>
                    {`(${count} × ${pointValue} = ${points})`}
                  </p>
                )}
                {isHierarchical && (
                  <p style={{ fontSize: '0.85em', color: '#8a90a0' }}>
                    {`(~${count} activities across team)`}
                  </p>
                )}
              </div>
            );
          })}
          {totalPoints > 0 && (
            <div style={{ marginTop: '5px', paddingTop: '5px', borderTop: '1px solid #3d4455' }}>
              <p style={{ fontWeight: 'bold', color: '#e8eaed' }}>
                Total: {totalPoints} points
                {isHierarchical && <span style={{ fontSize: '0.85em', fontWeight: 'normal' }}> (team aggregate)</span>}
              </p>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  const TotalLabel = (props) => {
    const { x, y, width, payload } = props;
    
    if (!payload || typeof payload.total === 'undefined') return null;
    
    const total = payload.total;
    if (total === 0) return null;
    
    return (
      <text
        x={x + width / 2}
        y={y - 5}
        fill="#e8eaed"
        textAnchor="middle"
        fontSize="12"
        fontWeight="bold"
      >
        {total}
      </text>
    );
  };

  if (loading) {
    return (
      <div className="admin-statistics">
        <div className="header">
          <button onClick={onBack} className="back-button">← Back to Dashboard</button>
          <h2>Team Statistics</h2>
        </div>
        <div className="loading">Loading statistics...</div>
      </div>
    );
  }

  if (managedUsers.length === 0) {
    return (
      <div className="admin-statistics">
        <div className="header">
          <button onClick={onBack} className="back-button">← Back to Dashboard</button>
          <h2>Team Statistics</h2>
        </div>
        <div className="no-users">You don't have any claimed workers yet.</div>
      </div>
    );
  }

  return (
    <div className="admin-statistics">
      <div className="header">
        <button onClick={onBack} className="back-button">← Back to Dashboard</button>
        <h2>Team Statistics</h2>
      </div>
      
      <div className="chart-container">
        <div className="user-selector">
          <label htmlFor="user-select">Select Worker:</label>
          <select 
            id="user-select"
            value={selectedUserId} 
            onChange={handleUserChange}
            className="user-select"
          >
            {managedUsers.map(user => (
              <option key={user.id} value={user.id}>
                {user.username} {user.is_superuser ? '(Admin)' : ''} - {user.profile?.first_name} {user.profile?.last_name}
              </option>
            ))}
          </select>
        </div>

        {selectedUser && (
          <div className="selected-user-info">
            <h3>
              Statistics for {selectedUser.username}
              {isHierarchical && (
                <span className="hierarchy-badge">+ Team Hierarchy</span>
              )}
            </h3>
            {selectedUser.profile && (selectedUser.profile.first_name || selectedUser.profile.last_name) && (
              <p className="user-name">
                {selectedUser.profile.first_name} {selectedUser.profile.last_name}
              </p>
            )}
            {hierarchyInfo && isHierarchical && (
              <div className="hierarchy-info">
                <p className="hierarchy-details">
                  Showing aggregate data for {hierarchyInfo.total_users_in_hierarchy} users in hierarchy
                  {hierarchyInfo.circular_reference_excluded && (
                    <span className="circular-note"> (circular reference excluded)</span>
                  )}
                </p>
                <details className="hierarchy-users">
                  <summary>View hierarchy users</summary>
                  <ul>
                    {hierarchyInfo.hierarchy_users.map(hierarchyUser => (
                      <li key={hierarchyUser.id} className={hierarchyUser.is_target ? 'target-user' : ''}>
                        {hierarchyUser.username}
                        {hierarchyUser.is_superuser && <span className="admin-badge-small">Admin</span>}
                        {hierarchyUser.is_target && <span className="target-badge">Target</span>}
                        {hierarchyUser.full_name && <span className="full-name">({hierarchyUser.full_name})</span>}
                      </li>
                    ))}
                  </ul>
                </details>
              </div>
            )}
          </div>
        )}

        <div className="controls">
          <div className="aggregation-controls">
            <label htmlFor="aggregation-type">Aggregation:</label>
            <select 
              id="aggregation-type"
              value={aggregationType} 
              onChange={handleAggregationChange}
              className="aggregation-select"
            >
              <option value="day">Daily</option>
              <option value="week">Weekly</option>
              <option value="month">Monthly</option>
            </select>
          </div>
          
          <form onSubmit={handlePeriodsSubmit} className="periods-form">
            <label htmlFor="num-periods">
              Show last {aggregationType === 'day' ? 'days' : aggregationType === 'week' ? 'weeks' : 'months'}:
            </label>
            <input
              id="num-periods"
              type="number"
              min="1"
              max="100"
              value={tempPeriods}
              onChange={(e) => setTempPeriods(e.target.value)}
              className="periods-input"
            />
            <button type="submit" className="apply-button">Apply</button>
          </form>
        </div>

        {error && <div className="error">{error}</div>}

        <div className="chart">
          <ResponsiveContainer width="100%" height={450}>
            <BarChart
              data={chartData}
              margin={{ top: 40, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#3d4455" />
              <XAxis
                dataKey="label"
                angle={aggregationType === 'day' ? -45 : 0}
                textAnchor={aggregationType === 'day' ? 'end' : 'middle'}
                height={aggregationType === 'day' ? 80 : 50}
                tick={{ fill: '#b0b5c0', fontSize: 14 }}
                axisLine={{ stroke: '#454c5e' }}
              />
              <YAxis
                label={{ value: 'Points', angle: -90, position: 'insideLeft', fill: '#b0b5c0', fontSize: 14 }}
                tick={{ fill: '#b0b5c0', fontSize: 14 }}
                axisLine={{ stroke: '#454c5e' }}
              />
              <Tooltip content={customTooltip} cursor={{ fill: 'rgba(255, 255, 255, 0.05)' }} />
              <Legend />
              {fieldOrder.map(field => (
                <Bar
                  key={field}
                  dataKey={field}
                  stackId="a"
                  fill={fieldColors[field]}
                  name={fieldLabels[field]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default AdminStatistics;